import React from 'react';
import { CellProps, Column } from 'react-table';
import { useToggle } from '@mantine/hooks';

import { TrackableItem, TrackableFile } from 'types/trackables';

import { useTrackableFiles } from '../../hooks/useTrackableFiles';
import { useDeleteTrackableFiles } from '../../hooks/useDeleteTrackableFiles';
import { useModal } from 'hooks/useModal';

import { Button } from 'components/Button/Button';
import { EmptyView } from 'components/EmptyView/EmptyView';
import { Loader } from 'components/Loader/Loader';
import { NameCell } from 'components/Table/Table';
import { PaginatedTable } from 'components/Table/PaginatedTable';

import { AddTrackableFileDialog } from '../AddTrackableFileDialog';
import { EditTrackableFileDialog } from '../EditTrackableFileDialog';
import { TrackableItemFilesActionsCell } from './TrackableItemFilesActionsCell';

import { relativeTimeFromNow } from 'services/date';
import { pluralize } from 'services/string';

export interface TrackableItemFilesProps {
  trackableItem: TrackableItem;
  trackableListId: string;
  openUploadOnMount?: boolean;
  onUploadDialogOpened?: () => void;
}

export function TrackableItemFiles({
  trackableItem,
  trackableListId,
  openUploadOnMount,
  onUploadDialogOpened,
}: TrackableItemFilesProps) {
  const [isUploadingFiles, toggleIsUploadingFiles] = useToggle();
  const [editingFile, setEditingFile] = React.useState<TrackableFile | null>(
    null
  );
  const [page, setPage] = React.useState(1);

  // Open upload dialog on mount if requested (e.g., after creating a new item)
  React.useEffect(() => {
    if (openUploadOnMount) {
      toggleIsUploadingFiles(true);
      // Notify parent that the dialog has been opened so it can clear the navigation state
      onUploadDialogOpened?.();
    }
  }, [openUploadOnMount, toggleIsUploadingFiles, onUploadDialogOpened]);

  const { open } = useModal();
  const deleteTrackableFiles = useDeleteTrackableFiles();

  const pageSize = 50;
  const queryResult = useTrackableFiles({
    params: {
      trackable_list_id: trackableListId,
      trackable_item_id: trackableItem.id,
      with_thumbnail: true,
      pagination: 'offset',
      page,
      limit: pageSize,
    },
  });

  const columns: Column<TrackableFile>[] = React.useMemo(
    () => [
      {
        Header: 'Thumbnail',
        accessor: 'thumbnail_url',
        Cell: ({
          value,
        }: CellProps<TrackableFile, TrackableFile['thumbnail_url']>) => (
          <div className="flex h-11 w-20 items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-transparent dark:border-gray-700">
            {value ? (
              <img
                src={value}
                alt="Thumbnail"
                className="h-full w-full rounded-md object-contain"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center rounded-md bg-gray-100 dark:bg-gray-800">
                <span className="text-xs text-gray-400">No image</span>
              </div>
            )}
          </div>
        ),
        disableSortBy: true,
        disableGlobalFilter: true,
        width: 100,
      },
      {
        Header: 'Name',
        accessor: 'name',
        Cell: (props: CellProps<TrackableFile>) => (
          <NameCell {...props} entity="watchlist file" />
        ),
        sortType: 'string',
        disableGlobalFilter: false,
      },
      {
        Header: 'Description',
        accessor: 'description',
        Cell: ({
          value,
        }: CellProps<TrackableFile, TrackableFile['description']>) => (
          <span className="text-gray-600 dark:text-gray-400">
            {value || '—'}
          </span>
        ),
        sortType: 'string',
        disableGlobalFilter: false,
      },
      {
        Header: 'Created',
        accessor: 'created_at',
        Cell: ({
          value,
        }: CellProps<TrackableFile, TrackableFile['created_at']>) => (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {relativeTimeFromNow(value)}
          </span>
        ),
        sortType: 'datetime',
        disableGlobalFilter: true,
      },
      {
        Header: '',
        accessor: 'id',
        Cell: (props: CellProps<TrackableFile>) => (
          <TrackableItemFilesActionsCell
            {...props}
            trackableListId={trackableListId}
            trackableItemId={trackableItem.id}
            onEdit={setEditingFile}
          />
        ),
        disableSortBy: true,
        disableGlobalFilter: true,
        width: 120,
      },
    ],
    [trackableListId, trackableItem.id]
  );

  const bulkActions = React.useMemo(
    () => [
      {
        intent: 'danger' as const,
        action: 'Delete',
        loading: deleteTrackableFiles.isLoading,
        onClick: (ids: string[]) => {
          open('confirm-delete', {
            title: `Are you sure you want to delete ${pluralize(ids.length, 'this', String(ids.length))} ${pluralize(ids.length, 'trackable file')}?`,
            onConfirm: () =>
              deleteTrackableFiles.mutate({
                trackable_list_id: trackableListId,
                trackable_item_id: trackableItem.id,
                trackable_file_ids: ids,
              }),
          });
        },
      },
    ],
    [open, deleteTrackableFiles, trackableListId, trackableItem.id]
  );

  const hasFiles = queryResult.data?.data?.length ?? 0 > 0;

  const renderContent = () => {
    if (queryResult.isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader size="small" text="Loading images..." />
        </div>
      );
    }

    if (!hasFiles) {
      return (
        <div className="p-6">
          <EmptyView
            title="No images found"
            buttonText="Add Images"
            onButtonClick={() => toggleIsUploadingFiles()}
          >
            Add reference images to help identify this item in video streams.
          </EmptyView>
        </div>
      );
    }

    return (
      <div className="isolation-isolate grid h-full w-full grid-rows-[auto_1fr] overflow-hidden">
        {/* Header section - similar to DeploymentListSmall .top */}
        <div className="z-10 flex flex-row items-center gap-4 border-b border-gray-200 bg-gray-100 px-6 py-4 dark:border-gray-700 dark:bg-gray-900">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Images used for tracking
          </span>
          <div className="flex-grow" />
          <Button
            variant="primary"
            size="small"
            onClick={() => toggleIsUploadingFiles()}
          >
            Add Images
          </Button>
        </div>

        {/* Table section - full width with scroll */}
        <div className="overflow-auto">
          <PaginatedTable
            queryResult={queryResult}
            pageSize={pageSize}
            page={page}
            onPageChange={setPage}
            id="trackable-files-detail-table"
            columns={columns}
            bulkActions={bulkActions}
            emptyMessage={
              <EmptyView title="No images found">
                No trackable images are available.
              </EmptyView>
            }
            label="Trackable Images"
            onRowClick={(file) => {
              // Open lightbox for any file with an ID
              if (file.id) {
                // Use requestAnimationFrame to ensure the modal opens in the next frame
                requestAnimationFrame(() => {
                  open('file', { id: file.id });
                });
              }
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {renderContent()}

      {/* Dialog rendered in consistent location regardless of files state */}
      <AddTrackableFileDialog
        trackableListId={trackableListId}
        trackableItemId={trackableItem.id}
        itemName={trackableItem.name}
        open={isUploadingFiles}
        onClose={toggleIsUploadingFiles}
      />

      {editingFile && (
        <EditTrackableFileDialog
          trackableFile={editingFile}
          trackableListId={trackableListId}
          trackableItemId={trackableItem.id}
          open={!!editingFile}
          onClose={() => setEditingFile(null)}
        />
      )}
    </>
  );
}
